/** Normalised key used to merge the same word/expression across episodes. */
export function normalizeTerm(text: string): string {
  return text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[^a-z0-9'\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** ECDICT exchange field, e.g. "p:went/d:gone/0:go/1:p" → "go". */
export function lemmaFromExchange(word: string, exchange: string | null | undefined): string {
  const base = exchange?.split('/').find((part) => part.startsWith('0:'))?.slice(2);
  return normalizeTerm(base || word);
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Locate the term (allowing simple inflections such as pressure → pressures, step → stepped). */
export function findTerm(sentence: string, term: string): { start: number; end: number } | null {
  const words = term.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  const parts = words.map((w) => escapeRegExp(w) + (w.length >= 3 ? '[a-z]{0,3}' : ''));
  const match = new RegExp(`\\b${parts.join("[\\s'’-]+")}\\b`, 'i').exec(sentence);
  return match ? { start: match.index, end: match.index + match[0].length } : null;
}

/** Replace the term in its original sentence with a blank for review. */
export function clozeSentence(sentence: string, term: string): { text: string; found: boolean } {
  const at = findTerm(sentence, term);
  if (!at) return { text: sentence, found: false };
  const blank = sentence.slice(at.start, at.end).replace(/[A-Za-z]/g, '_').replace(/_+/g, '____');
  return { text: sentence.slice(0, at.start) + blank + sentence.slice(at.end), found: true };
}

export interface CopySource {
  term: string;
  meaning?: string;
  en: string;
  zh?: string;
  program?: string;
  episodeTitle?: string;
  date?: string;
}

export function formatForCopy(s: CopySource): string {
  const head = s.meaning ? `${s.term} — ${s.meaning}` : s.term;
  const source = [s.program, s.episodeTitle, s.date].filter(Boolean).join(' · ');
  return [head, `"${s.en}"`, s.zh, source && `(${source})`].filter(Boolean).join('\n');
}
